import {
  useRef,
  useState,
  useEffect,
  useContext,
  ReactNode,
  RefObject,
  useCallback,
} from 'react';
import ContentEditable, { ContentEditableEvent } from 'react-contenteditable';
import { Block, TtsContext } from '../../../../contexts/TtsContext';
import {
  BlockActionButton,
  BlockEffectsContainer,
  EditableBlockContainer,
  SelectionMarkers,
  SentenceTooltip,
  VoiceLabel,
} from './styles';
import {
  Equalizer,
  WaveSine,
  Gauge,
  PlayCircle,
  MagicWand,
  KeyReturn,
  Pause,
} from '@phosphor-icons/react';
import { ControlLabel } from './ControlLabel';
import * as SpeechSDK from 'microsoft-cognitiveservices-speech-sdk';
import { generateSsml } from '../../../../lib/generateSsml';
import { AiContext } from '../../../../contexts/AiContext';
import { SpeakerModal } from '../../Toolbox/SpeakerModal';
import { toast } from 'react-toastify';
import { EffectSelection, useStore } from '../../../../store';
import { useTranslation } from 'react-i18next';
import { TutorialsContext } from '../../../../contexts/TutorialsContext';
import { useAppInsightsContext } from '@microsoft/applicationinsights-react-js';
import { app } from '@microsoft/teams-js';
import e from 'express';

interface BlockProps {
  id: string;
  index: number;
  block: Block;
  audioRef: RefObject<HTMLAudioElement>;
}

interface AppliedControl {
  icon: ReactNode;
  type: string;
}

export interface SelectionRange {
  start: number;
  end: number;
}

export const EditableBlock = ({ id, index, block, audioRef }: BlockProps) => {
  const appInsights = useAppInsightsContext();
  const [context, setContext] = useState<app.Context>();

  useEffect(() => {
    app.getContext().then((context) => {
      setContext(context);
    });
  }, []);

  const { t } = useTranslation('myapps');

  const {
    blocks,

    updateBlockContent,
    addBlock,
    deleteBlock,
    convertToBlocks,

    deleteEffectSelection,

    hasAddedBlock,
    setHasAddedBlock,

    cachedAudios,
    setCachedAudios,

    setIsLoading,
  } = useStore();

  const [selection, setSelection] = useState<SelectionRange>();

  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const tooltipHasAppeared = useRef(false);

  const [isSpeakerModalOpen, setIsSpeakerModalOpen] = useState(false);
  const openSpeakerModal = () => setIsSpeakerModalOpen(true);
  const closeSpeakerModal = () => setIsSpeakerModalOpen(false);

  const { status } = useContext(TutorialsContext);

  const { showAiTools } = useContext(AiContext);

  const {
    showToolbox,
    hideToolbox,
    isToolboxVisible,
    speechToken,
    limitReached,
    setAudioTrack,
  } = useContext(TtsContext);

  const [previousKey, setPreviousKey] = useState<string>('');

  const blockRef = useRef<HTMLElement>(null);
  const rectRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (hasAddedBlock) {
      const nextBlock =
        hasAddedBlock.ref?.parentElement?.nextElementSibling?.querySelector(
          '.sentence-block'
        ) as HTMLElement;
      if (nextBlock && nextBlock.innerHTML === '') {
        nextBlock.focus();
      }
    }

    return () => {
      setHasAddedBlock(undefined);
    };
  }, [hasAddedBlock, setHasAddedBlock]);

  const renderSelection = (sel: EffectSelection) => {
    try {
      const text = blocks[index].html;

      if (
        text &&
        blockRef.current?.firstChild &&
        blockRef.current.firstChild.textContent &&
        blockRef.current.firstChild.textContent.length >= sel.end
      ) {
        const range = document.createRange();

        range.setStart(blockRef.current.firstChild, sel.start);
        range.setEnd(blockRef.current.firstChild, sel.end);

        const clientRect = range.getBoundingClientRect();
        const relativeX =
          clientRect.x - blockRef.current.getBoundingClientRect().x + 60;
        const relativeY =
          clientRect.y - blockRef.current.getBoundingClientRect().y;
        const viewportWidth = clientRect.width;
        const viewportHeight = clientRect.height;

        return {
          relativeX,
          relativeY,
          viewportWidth,
          viewportHeight,
        };
      }
    } catch (err: any) {
      if (process.env.REACT_APP_APP_INSIGHTS !== '')
        appInsights.trackException({
          exception: err,
          properties: { component: 'TtsApp', location: 'onRenderSelections' },
        });
    }
  };

  const renderControlLabels = (): AppliedControl[] => {
    const applied = [];

    block.controls.pitch !== 'default' &&
      applied.push({ icon: <WaveSine />, type: 'pitch' } as never);

    block.controls.volume !== 'default' &&
      applied.push({ icon: <Equalizer />, type: 'volume' } as never);

    block.controls.speed !== 'default' &&
      applied.push({ icon: <Gauge />, type: 'speed' } as never);

    block.controls.style &&
      applied.push({ icon: <MagicWand />, type: 'style' } as never);

    block.controls.break &&
      block.controls.break.value !== 'default' &&
      applied.push({ icon: <Pause />, type: 'break' } as never);

    return applied;
  };

  const onChangeHandler = (e: ContentEditableEvent) => {
    updateBlockContent(id, e.target.value);

    const target = window.getSelection()?.focusOffset;

    if (blockRef.current && target) {
      blockRef.current.focus();

      const range = document.createRange();
      const selection = window.getSelection();
      const textNode = blockRef.current.firstChild;

      if (
        textNode?.textContent &&
        selection?.type === 'range' &&
        textNode.textContent.length >= target
      ) {
        range.setStart(textNode, target);
        range.setEnd(textNode, target);

        selection.removeAllRanges();
        selection.addRange(range);
      }
    }

    if (target !== undefined) {
      const isInSelection = blocks[index]?.selections.filter(
        (sel) => target < sel.end
      );

      if (isInSelection) {
        deleteEffectSelection(
          id,
          isInSelection.map((s) => s.id)
        );

        return;
      }
    }
  };

  const onKeyDownHandler = (e: any) => {
    if (e.key === ' ') {
      if (tooltipHasAppeared.current === false) {
        setIsTooltipVisible(true);

        setTimeout(() => {
          setIsTooltipVisible(false);
        }, 5000);

        tooltipHasAppeared.current = true;
      }
    }

    if (e.key === '/') {
      if (previousKey !== 'Shift') {
        e.preventDefault();
        showAiTools();
      }
    }
    if (e.key === 'Enter') {
      setIsTooltipVisible(false);

      if (previousKey !== 'Shift') {
        e.preventDefault();

        let cursorPosition = 0;
        const inputElement = blockRef.current;
        const selection = window.getSelection();
        if (inputElement && selection) {
          const range = selection.getRangeAt(0);
          const clonedRange = range.cloneRange();
          clonedRange.selectNodeContents(inputElement);
          clonedRange.setEnd(range.endContainer, range.endOffset);
          cursorPosition = clonedRange.toString().length;
        }

        if (cursorPosition < blocks[index].html.length) {
          const htmlBefore = blocks[index].html.substring(0, cursorPosition);
          const htmlAfter = blocks[index].html.substring(cursorPosition);

          blocks[index].html = htmlBefore;
          updateBlockContent(id, htmlBefore);
          addBlock(
            {
              id,
              ref: blockRef.current,
              index,
            },
            htmlAfter
          );
        } else {
          addBlock(
            {
              id,
              ref: blockRef.current,
              index,
            },
            ''
          );
        }
      }
    }
    if (e.key === 'Backspace') {
      if (!blocks[index].html) {
        e.preventDefault();
        deleteBlock({
          id,
          ref: blockRef.current,
          index,
        });
      }
    }
    setPreviousKey(e.key);
  };

  const onPasteHandler = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const content = e.clipboardData.getData('text/plain');
    let cursorPosition = 0;
    let rangeCount = 0;
    const inputElement = blockRef.current;
    const selection = window.getSelection();
    if (inputElement && selection) {
      const range = selection.getRangeAt(0);
      const clonedRange = range.cloneRange();
      clonedRange.selectNodeContents(inputElement);
      clonedRange.setEnd(range.endContainer, range.endOffset);
      cursorPosition = clonedRange.toString().length;
      rangeCount = cursorPosition - range.startOffset;
    }
    convertToBlocks(content, id, cursorPosition, rangeCount);
  };

  const onSelectionHandler = useCallback(
    (e?: React.SyntheticEvent) => {
      const sel = window.getSelection();

      if (e?.type === 'blur') {
        setSelection(undefined);
      }

      if (
        sel &&
        sel.type !== 'None' &&
        sel.type !== 'Caret' &&
        sel.focusNode?.parentElement?.id === `content-${id}`
      ) {
        const range = sel.getRangeAt(0);
        let selectedText = sel.toString();

        let start =
          sel.anchorOffset > sel.focusOffset
            ? sel.focusOffset
            : sel.anchorOffset;
        let end = range.endOffset;

        for (let i = 0; selectedText.charAt(0) === ' '; i++) {
          selectedText = selectedText.substring(1);
          start++;
        }

        for (
          let i = 0;
          selectedText.charAt(selectedText.length - 1) === ' ';
          i++
        ) {
          selectedText = selectedText.substring(0, selectedText.length - 1);
          end--;
        }

        const fullText = sel.anchorNode?.textContent;

        if (fullText) {
          const isFullWordSelection =
            start >= 0 &&
            (fullText[start - 1] === ' ' ||
              fullText[start - 1] === undefined) &&
            (fullText[end] === ' ' || fullText[end] === undefined);

          if (!isFullWordSelection) {
            while (start > 0 && fullText[start - 1] !== ' ') {
              start--;
            }
            while (end < fullText.length && fullText[end] !== ' ') {
              end++;
            }
          }
        }

        if (start !== end) {
          setSelection({ start, end });
        } else {
          setSelection(undefined);
        }
      } else {
        setSelection(undefined);
      }
    },
    [id]
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        blockRef.current &&
        !blockRef.current.contains(event.target as Node)
      ) {
        onSelectionHandler();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onSelectionHandler]);

  const playBlock = () => {
    if (limitReached) {
      toast('Characters limit reached', { type: 'error' });
      return;
    }

    if (block) {
      if (cachedAudios) {
        const hasLocalAudio = cachedAudios.find(
          (audio) => audio.blockId === id
        );

        if (hasLocalAudio && !hasLocalAudio.hasChanged) {
          setAudioTrack(hasLocalAudio.url);

          audioRef.current &&
            audioRef.current.ended &&
            audioRef.current &&
            audioRef.current
              .play()
              .catch(() =>
                console.error('failed to play audio, please try again.')
              );

          return;
        }

        setIsLoading(true);

        const ssml = generateSsml([block]);

        if (speechToken) {
          try {
            const speechConfig = SpeechSDK.SpeechConfig.fromAuthorizationToken(
              speechToken.token,
              speechToken.region
            );

            speechConfig.speechSynthesisVoiceName = block.controls.speaker.name;
            speechConfig.speechSynthesisOutputFormat =
              SpeechSDK.SpeechSynthesisOutputFormat.Audio24Khz48KBitRateMonoMp3;

            const synthesizer = new SpeechSDK.SpeechSynthesizer(
              speechConfig,
              null as unknown as SpeechSDK.AudioConfig
            );

            synthesizer.synthesisCompleted = (s, e) => {
              s.close();

              const url = window.URL.createObjectURL(
                new Blob([e.result.audioData])
              );

              if (hasLocalAudio) {
                const newAudios = cachedAudios.filter(
                  (audio) => audio.blockId !== id
                );

                newAudios.push({
                  blockId: hasLocalAudio.blockId,
                  url: url,
                  hasChanged: false,
                });

                setCachedAudios(newAudios);
              } else {
                setCachedAudios([
                  ...cachedAudios,
                  { blockId: id, url, hasChanged: false },
                ]);
              }

              setAudioTrack(url);
              setIsLoading(false);
            };

            synthesizer.SynthesisCanceled = (s, e) => {
              s.close();
              setIsLoading(false);
            };

            synthesizer.speakSsmlAsync(ssml);
            if (process.env.REACT_APP_APP_INSIGHTS !== '')
              appInsights.trackEvent(
                { name: 'Audio Synthesis iniated' },
                {
                  tid: context?.user?.tenant?.id,
                  uid: context?.user?.id,
                  datasize: ssml.length,
                }
              );
          } catch (err: any) {
            if (process.env.REACT_APP_APP_INSIGHTS !== '')
              appInsights.trackException({
                exception: err,
                properties: {
                  component: 'TtsApp',
                  location: 'onGenerateAudio',
                  isAudioready: false,
                },
              });
          }
        }
      }
    }
  };

  return (
    <EditableBlockContainer
      id={index === 0 ? 'step-start-typing' : ''}
      index={index}
      draggable={false}
      onDragStart={(ev) => ev.preventDefault()}
      runningTutorial={status === 'running'}
    >
      <BlockActionButton
        id="step-apply-effects"
        onClick={(ev) => {
          onSelectionHandler(ev);

          if (isToolboxVisible) {
            hideToolbox();
          } else {
            showToolbox({
              block: blockRef.current,
              id,
              openModal: openSpeakerModal,
              selection,
            });
          }
        }}
      >
        <MagicWand size={24} weight="duotone" />
      </BlockActionButton>

      <BlockActionButton
        onClick={playBlock}
        id="step-run-sentence-audio-effects"
      >
        <PlayCircle size={24} weight="duotone" />
      </BlockActionButton>

      <ContentEditable
        className="sentence-block"
        innerRef={blockRef}
        html={blocks[index].html}
        tagName="p"
        onPaste={onPasteHandler}
        onChange={(ev) => onChangeHandler(ev)}
        onKeyDown={onKeyDownHandler}
        onMouseUp={onSelectionHandler}
        onFocus={onSelectionHandler}
        onBlur={onSelectionHandler}
        id={'content-' + id}
      />

      {
        <SentenceTooltip
          isVisible={isTooltipVisible}
          onDragStartCapture={(ev) => ev.preventDefault()}
        >
          {t('TTS_NEW_BLOCK_TOOLTIP_1')} <KeyReturn weight="thin" />{' '}
          {t('TTS_NEW_BLOCK_TOOLTIP_2')}
        </SentenceTooltip>
      }

      <SelectionMarkers
        ref={rectRef}
        onDragStartCapture={(ev) => ev.preventDefault()}
      >
        {blocks[index].selections.map((selection) => {
          const rect = renderSelection(selection);

          return (
            <div
              className="selection"
              id={`selection-${selection.id}`}
              style={{
                position: 'absolute',
                left: `${rect?.relativeX}px`,
                top: `${rect?.relativeY}px`,
                width: `${rect?.viewportWidth}px`,
                height: `${rect?.viewportHeight}px`,
              }}
              onClick={() => {
                setSelection({ start: selection.start, end: selection.end });
                showToolbox({
                  block: blockRef.current,
                  id,
                  selection_id: selection.id,
                  openModal: openSpeakerModal,
                  selection,
                });
              }}
            />
          );
        })}
      </SelectionMarkers>

      <BlockEffectsContainer onDragStartCapture={(ev) => ev.preventDefault()}>
        {renderControlLabels().map((control) => (
          <ControlLabel
            icon={control.icon}
            blockId={id}
            control={control.type}
          />
        ))}
      </BlockEffectsContainer>

      {block && block.controls?.speaker && (
        <>
          <VoiceLabel
            onClick={() => openSpeakerModal()}
            onDragStartCapture={(ev) => ev.preventDefault()}
          >
            {block.controls.speaker.key}
          </VoiceLabel>
          <SpeakerModal
            blockId={id}
            isOpen={isSpeakerModalOpen}
            closeModal={closeSpeakerModal}
          />
        </>
      )}
    </EditableBlockContainer>
  );
};
